import { Popover } from '@headlessui/react';
import { useState } from 'react';
import {
  icons,
  KoobEmptyState,
  KoobInputSegmented,
  KoobLoader,
  KoobTooltip,
} from '@koob/ui';
import { useTranslation } from 'react-i18next';
import NotificationPopoverListItem from './NotificationPopoverListItem';

function HeaderButton({ icon, label, onClick, disabled }) {
  return (
    <KoobTooltip tip={label}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-k-p transition hover:bg-gray-100 hover:text-k-h disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className={`${icon} text-sm`} />
      </button>
    </KoobTooltip>
  );
}

export default function NotificationPopover({
  notifications,
  loading,
  reload,
  markAsRead,
  markAllAsRead,
  navigate,
  scope,
}) {
  const { t } = useTranslation('notifications');
  const [unreadOnly, setUnreadOnly] = useState(true);

  const unreadCount = notifications?.filter((n) => !n.readAt)?.length ?? 0;
  const shown = unreadOnly
    ? notifications?.filter((n) => !n.readAt)
    : notifications;
  const empty =
    unreadOnly && notifications?.length > 0 ? 'allRead' : 'emptyState';

  // No polling here: refreshes come from the SSE push in NotificationsProvider.

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            aria-label={t('title')}
            className={`relative inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border-none transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
              open
                ? 'bg-gray-100 text-k-h'
                : 'bg-transparent text-k-p hover:bg-gray-100 hover:text-k-h'
            }`}
          >
            <i className={`${icons.notification} text-xl`} />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 inline-flex h-[1.15rem] min-w-[1.15rem] items-center justify-center rounded-full border-2 border-solid border-white bg-orange-500 px-1 text-[0.65rem] font-semibold leading-none text-k-h">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Popover.Button>

          <Popover.Panel className="absolute right-0 top-full z-popover mt-2 flex w-[420px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-solid border-gray-100 bg-white shadow-lg">
            {({ close }) => (
              <>
                <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-gray-100 px-4 py-3">
                  <h2 className="m-0 text-sm font-bold text-k-h">
                    {t('title')}
                  </h2>
                  <div className="flex items-center gap-1">
                    <HeaderButton
                      icon={icons.check}
                      label={t('markAllAsRead')}
                      onClick={() => markAllAsRead()}
                      disabled={!unreadCount}
                    />
                    <HeaderButton
                      icon={icons.refresh}
                      label={t('refresh')}
                      onClick={() => reload()}
                      disabled={loading}
                    />
                    <HeaderButton
                      icon={icons.settings}
                      label={t('preferences')}
                      onClick={() => {
                        close();
                        navigate('/user/notification-preferences');
                      }}
                    />
                  </div>
                </div>

                <div className="border-0 border-b border-solid border-gray-100 px-4 py-2">
                  <KoobInputSegmented
                    size="sm"
                    value={unreadOnly}
                    onChange={setUnreadOnly}
                    options={[
                      {
                        value: true,
                        label: `${t('unread')} (${unreadCount})`,
                      },
                      { value: false, label: t('all') },
                    ]}
                  />
                </div>

                {loading && !notifications?.length ? (
                  <div className="h-72">
                    <KoobLoader centered />
                  </div>
                ) : shown?.length > 0 ? (
                  <div className="max-h-96 divide-y divide-solid divide-gray-100 overflow-y-auto">
                    {shown.map((notification) => (
                      <NotificationPopoverListItem
                        key={notification.id}
                        notification={notification}
                        markAsRead={markAsRead}
                        navigate={navigate}
                        scope={scope}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex h-72 items-center justify-center">
                    <KoobEmptyState
                      variant="plain"
                      icon={<i className={`${icons.sparkles} text-2xl`} />}
                      title={t(`${empty}.title`)}
                      description={t(`${empty}.description`)}
                    />
                  </div>
                )}
              </>
            )}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}
